import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    getCustomersAndSites,
    getUserById,
    getSalesDistrict,
    getAccountsLite,
    CustomerAccountLite,
} from '../../Services/serviceURL';
import type { AddUserFormData, CustomerData, CustomerLite, SalesDistrict, SelectionMap } from './addUserTypes';
import {
    accessVisibilityToSelectionMap,
    mapApiUserToFormData,
} from './addUserFormInput';
import { customerHasParentSelection, updateParentSelections } from './addUserSelection';

type EffectsParams = {
    isEditMode: boolean;
    userId?: string;
    userDataLoaded: boolean;
    setUserDataLoaded: (value: boolean) => void;
    setLoadingUserData: (value: boolean) => void;
    setFormData: React.Dispatch<React.SetStateAction<AddUserFormData>>;
    setShowEncryptedPassword: (value: boolean) => void;
    setSelectedItems: React.Dispatch<React.SetStateAction<SelectionMap>>;
    setCustomers: React.Dispatch<React.SetStateAction<CustomerLite[]>>;
    setCustomersAndSites: React.Dispatch<React.SetStateAction<CustomerData[]>>;
    filteredCustomersFromHook: CustomerData[];
    setFilteredCustomers: React.Dispatch<React.SetStateAction<CustomerData[]>>;
    customersAndSites: CustomerData[];
    setDistricts: React.Dispatch<React.SetStateAction<SalesDistrict[]>>;
    setLoadingDistricts: (value: boolean) => void;
    setErrorDistricts: React.Dispatch<React.SetStateAction<string | null>>;
    formData: AddUserFormData;
    isEditModeForExternalReset: boolean;
    setExpandedItems: React.Dispatch<React.SetStateAction<SelectionMap>>;
};

export function useAddUserPageEffects(params: EffectsParams) {
    const navigate = useNavigate();
    const {
        isEditMode,
        userId,
        userDataLoaded,
        setUserDataLoaded,
        setLoadingUserData,
        setFormData,
        setShowEncryptedPassword,
        setSelectedItems,
        setCustomers,
        setCustomersAndSites,
        filteredCustomersFromHook,
        setFilteredCustomers,
        customersAndSites,
        setDistricts,
        setLoadingDistricts,
        setErrorDistricts,
        formData,
        isEditModeForExternalReset,
        setExpandedItems,
    } = params;

    useEffect(() => {
        const fetchCustomers = async () => {
            try {
                const customerAccountsData = await getAccountsLite();
                if (!Array.isArray(customerAccountsData)) {
                    setCustomers([]);
                    return;
                }
                const validCustomers = customerAccountsData.map((customer: CustomerAccountLite) => ({
                    customerId: customer.customerId,
                    customerName: customer.customerName,
                    soldTo: customer.salesDistrictCustomer || customer.salesDistrict || '',
                }));
                setCustomers(validCustomers);
            } catch {
                setCustomers([]);
            }
        };
        fetchCustomers();
    }, [setCustomers]);

    useEffect(() => {
        const fetchCustomersAndSites = async () => {
            try {
                const response = await getCustomersAndSites();
                const data = response.Data || response;
                setCustomersAndSites(data);
            } catch {
                // ignore
            }
        };
        fetchCustomersAndSites();
    }, [setCustomersAndSites]);

    useEffect(() => {
        setFilteredCustomers(filteredCustomersFromHook);
    }, [filteredCustomersFromHook, setFilteredCustomers]);

    useEffect(() => {
        if (!isEditMode || !userId || userDataLoaded) {
            return;
        }
        setLoadingUserData(true);
        getUserById(userId)
            .then(response => {
                if (!response.Success || !response.Data) {
                    toast.error('User data not found');
                    navigate('/users');
                    return;
                }
                const newFormData = mapApiUserToFormData(response.Data);
                setFormData(newFormData);
                setUserDataLoaded(true);
                if (!newFormData.ssoEnabled) {
                    setShowEncryptedPassword(true);
                }
                setSelectedItems(accessVisibilityToSelectionMap(response.Data.accessVisibility));
            })
            .catch(() => {
                toast.error('Failed to load user data');
                navigate('/users');
            })
            .finally(() => {
                setLoadingUserData(false);
            });
    }, [
        isEditMode,
        userId,
        navigate,
        userDataLoaded,
        setFormData,
        setLoadingUserData,
        setSelectedItems,
        setShowEncryptedPassword,
        setUserDataLoaded,
    ]);

    useEffect(() => {
        if (customersAndSites.length === 0) {
            return;
        }
        setSelectedItems(current => {
            if (Object.keys(current).length === 0) {
                return current;
            }
            if (customerHasParentSelection(customersAndSites, current)) {
                return current;
            }
            return updateParentSelections(customersAndSites, current);
        });
    }, [customersAndSites, setSelectedItems]);

    useEffect(() => {
        const fetchDistricts = async () => {
            setLoadingDistricts(true);
            setErrorDistricts(null);
            try {
                const response = await getSalesDistrict();
                if (response.data.Success && Array.isArray(response.data.Data)) {
                    setDistricts(response.data.Data);
                } else {
                    setDistricts([]);
                    setErrorDistricts('Failed to load districts');
                }
            } catch (error: unknown) {
                setDistricts([]);
                const message = error instanceof Error ? error.message : 'Failed to load districts';
                setErrorDistricts(message);
            } finally {
                setLoadingDistricts(false);
            }
        };
        fetchDistricts();
    }, [setDistricts, setErrorDistricts, setLoadingDistricts]);

    useEffect(() => {
        if (formData.userType !== 'External') {
            return;
        }
        if (!isEditModeForExternalReset || !formData.customerId) {
            setExpandedItems({});
            setSelectedItems({});
        }
    }, [
        formData.customerId,
        formData.userType,
        isEditModeForExternalReset,
        setExpandedItems,
        setSelectedItems,
    ]);
}
